import { useState, useRef, useEffect } from "react";
import { streamAI, api } from "../lib/api";
import { Lightbulb, Mic, Square, Send, Sparkles, MessageSquare, FileText } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function IdeaEvaluatorPage() {
  const [mode, setMode] = useState("text"); // text | voice
  const [description, setDescription] = useState("");
  const [country, setCountry] = useState("UAE");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribe(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDescription(d => (d ? d + " " : "") + data.text);
      toast.success("Voice transcribed");
    } catch {
      toast.error("Transcription failed");
    }
    setTranscribing(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!description.trim()) { toast.error("Please describe your idea first"); return; }
    setOutput("");
    setStreaming(true);
    await streamAI("/ai/idea-evaluator", { description, country },
      t => setOutput(p => p + t),
      () => setStreaming(false),
      () => setStreaming(false));
  };

  return (
    <div data-testid="idea-evaluator-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <Seo
        title="AI Idea Evaluator — Test Any Business Idea in 60 Seconds | DealVest"
        description="Describe any business idea by voice or text. Our AI gives you global comparables, market size, revenue models, a simple business plan, valuation range, and a recommended path forward."
        path="/idea-evaluator"
      />
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="dv-pill mb-4"><Sparkles size={13} /> Free · Powered by AI</span>
        <h1 className="dv-display text-4xl md:text-5xl mb-3">Have an idea? Let AI evaluate it.</h1>
        <p className="text-lg text-slate-600">Describe your business idea — by typing or voice. Get a complete plan, valuation, and recommended path in 60 seconds.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 dv-shadow-soft" data-testid="idea-form">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Lightbulb size={20} className="text-blue-600" /></div>
            <h3 className="font-bold text-xl">Tell us your idea</h3>
          </div>

          <div className="flex gap-2 mb-4 p-1 bg-slate-100 rounded-xl">
            <button type="button" onClick={() => setMode("text")} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === "text" ? "bg-white shadow-sm text-blue-600" : "text-slate-500"}`} data-testid="mode-text">
              <MessageSquare size={14} className="inline mr-1.5" /> Type
            </button>
            <button type="button" onClick={() => setMode("voice")} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === "voice" ? "bg-white shadow-sm text-blue-600" : "text-slate-500"}`} data-testid="mode-voice">
              <Mic size={14} className="inline mr-1.5" /> Voice Note
            </button>
          </div>

          {mode === "voice" && (
            <div className="bg-blue-50 rounded-xl p-5 mb-4 text-center">
              <p className="text-sm text-slate-600 mb-3">Tap the button and describe your idea naturally.</p>
              {!recording ? (
                <button type="button" onClick={startRecording} disabled={transcribing} className="dv-btn dv-btn-primary mx-auto" data-testid="record-btn">
                  <Mic size={16} /> {transcribing ? "Transcribing..." : "Start recording"}
                </button>
              ) : (
                <button type="button" onClick={stopRecording} className="dv-btn dv-btn-primary mx-auto bg-red-600 hover:bg-red-700 dv-pulse-blue" data-testid="stop-btn">
                  <Square size={14} /> Stop & transcribe
                </button>
              )}
            </div>
          )}

          <div>
            <label className="dv-label">Your idea (in any words)</label>
            <textarea
              className="dv-input is-textarea"
              rows="6"
              required
              placeholder="Example: A mobile app to help small farmers in Kenya sell produce directly to restaurants in Nairobi..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              data-testid="idea-description"
            />
          </div>
          <div className="mt-3">
            <label className="dv-label">Primary country</label>
            <select className="dv-input" value={country} onChange={e => setCountry(e.target.value)} data-testid="idea-country">
              {["UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt", "Other"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <button type="submit" disabled={streaming || !description.trim()} className="dv-btn dv-btn-primary w-full mt-5" data-testid="evaluate-btn">
            <Send size={16} /> {streaming ? "Evaluating..." : "Evaluate my idea"}
          </button>
        </form>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-7 min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-xl flex items-center gap-2"><FileText size={18} className="text-blue-600" /> AI Evaluation Report</h3>
            {streaming && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">Streaming</span>}
          </div>
          <div className="text-[15px] text-slate-700 whitespace-pre-wrap leading-relaxed font-mono max-h-[640px] overflow-y-auto" data-testid="idea-output">
            {output || <span className="text-slate-400 font-sans">Submit your idea to receive a comprehensive evaluation including comparables, market size, valuation range, and recommended path forward.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
